import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { createUserContextHeader, debugUserContextHeader, WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import {
  Customer,
  Locale,
  Maybe,
  Role,
  SitesGQL,
  SiteState,
} from '@iss/libs/graphql';
import { DefaultContext } from '@apollo/client/core/types';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  Self,
  SimpleChanges,
} from '@angular/core';

import { Site, Sites } from './site-input.types';

import { TypeaheadSelectComponent } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-site-input',
  templateUrl: './site-input.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
  ],
})
export class SiteInputComponent implements OnChanges {
  private readonly query = inject(SitesGQL);

  public loading$!: Observable<boolean>;
  public items$!: Observable<Sites>;

  public readonly stringify = (sites: Sites | null): (id: Site['id'] | 'ALL') => string => {
    return id => id === 'ALL'
      ? 'All'
      : sites?.find(site => site.id === id)?.name || '';
  };

  public readonly matcher = (site: Site): string => site.name;

  public readonly content = (id: Site['id'] | 'ALL', sites: Sites | null): string | undefined =>
    id === 'ALL'
      ? 'All'
      : sites?.find(site => site.id === id)?.name;

  public readonly identity = (site: Site): Site['id'] => site.id;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public withAll: Maybe<boolean>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public forceTypeahead: Maybe<boolean>;
  @Input() public placeholder: Maybe<string>;
  @Input() public customerIds: Maybe<readonly Customer['id'][]>;
  @Input() public available: Maybe<readonly Site['id'][]>;
  @Input() public role: Maybe<Role>;
  @Input() public country: Maybe<Locale>;
  @Input() public siteStates: Maybe<readonly SiteState[]>;
  @Input() public multi: Maybe<boolean>;
  @Input() public loading: Maybe<boolean>;
  @Input() public clearable = true;

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.items$ || 'customerIds' in changes || 'siteStates' in changes || 'available' in changes || 'role' in changes || 'country' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    const query$ = this.query.watch({
      sitesFiltersInput: {
        offset: 0,
        limit: 999,
        customerIds: this.customerIds,
        siteStates: this.siteStates,
        ids: this.available,
      },
    }, {
      fetchPolicy: this.role ? 'no-cache' : undefined,
      context: this.context,
    }).valueChanges;

    this.loading$ = query$.pipe(map(({ loading }) => loading));
    this.items$ = query$.pipe(
      filter(({ data }) => Boolean(data)),
      map(({ data }) => data.sites.items),
    );
  }

  private get context(): DefaultContext | undefined {
    if (!this.role) return;

    const userContextHeader = createUserContextHeader({
      role: this.role,
      country: this.country,
      customer: this.customerIds?.at(0),
    });
    debugUserContextHeader('site-input', userContextHeader);

    return {
      headers: {
        [WO_CONTEXT_HEADER]: userContextHeader,
      },
    };
  }
}
